import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import API_URL from '../../apiConfig';

function Statistiques() {
  const [summary, setSummary] = useState(null);
  const [mostShared, setMostShared] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/dash/summary`, { credentials: "include" });
      const data = await res.json();
      if (data.success) {
        setSummary(data);
      }

      const resPop = await fetch(`${API_URL}/api/meditations/allmeditations`, { credentials: "include" });
      const dataPop = await resPop.json();
      if (dataPop.success) {
        const sorted = dataPop.meditations
          .sort((a, b) => (b.nbreShare || b.nbreshare || 0) - (a.nbreShare || a.nbreshare || 0))
          .slice(0, 5);
        setMostShared(sorted);
      }
    } catch (err) {
      console.error("Erreur stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "LAMP | Admin - Statistiques";
    fetchData();
  }, []);

  const statusData = summary?.statusStats ? summary.statusStats.map(s => ({
    name: Number(s.status || s.published) === 1 ? 'Publiées' : 'Brouillons',
    value: Number(s.count)
  })) : [
    { name: 'Publiées', value: 0 },
    { name: 'Brouillons', value: 0 }
  ];

  if (loading) {
      return (
          <div className="text-center py-5 mt-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="mt-2 text-muted">Chargement de vos statistiques...</p>
          </div>
      );
  }

  return (
    <div className="container-fluid py-4 animate__animated animate__fadeIn">
      <div className="row g-3 mb-4">
        {/* Cartes de résumé */}
        <div className="col-6 col-md-4 col-lg">
          <div className="card-custom p-3 text-center border-0 shadow-sm transition h-100 border-top border-primary border-4 bg-white rounded-4">
            <div className="bg-primary bg-opacity-10 text-primary rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
              <i className="bi bi-journal-text fs-6"></i>
            </div>
            <h3 className="h5 fw-bold mb-0 text-dark">
                {summary?.statusStats?.reduce((acc, curr) => acc + Number(curr.count), 0) || 0}
            </h3>
            <p className="extra-small text-muted mb-0 fw-bold">Méditations</p>
          </div>
        </div>
        
        <div className="col-6 col-md-4 col-lg">
          <div className="card-custom p-3 text-center border-0 shadow-sm transition h-100 border-top border-success border-4 bg-white rounded-4">
            <div className="bg-success bg-opacity-10 text-success rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
              <i className="bi bi-chat-dots fs-6"></i>
            </div>
            <h3 className="h5 fw-bold mb-0 text-dark">{summary?.totalMessages || 0}</h3>
            <p className="extra-small text-muted mb-0 fw-bold">Messages</p>
          </div>
        </div>

        <div className="col-6 col-md-4 col-lg">
          <div className="card-custom p-3 text-center border-0 shadow-sm transition h-100 border-top border-warning border-4 bg-white rounded-4">
            <div className="bg-warning bg-opacity-10 text-warning rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
              <i className="bi bi-people fs-6"></i>
            </div>
            <h3 className="h5 fw-bold mb-0 text-dark">{summary?.totalPartenaires || 0}</h3>
            <p className="extra-small text-muted mb-0 fw-bold">Partenaires</p>
          </div>
        </div>

        <div className="col-6 col-md-4 col-lg">
          <div className="card-custom p-3 text-center border-0 shadow-sm transition h-100 border-top border-info border-4 bg-white rounded-4">
            <div className="bg-info bg-opacity-10 text-info rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
              <i className="bi bi-star fs-6"></i>
            </div>
            <h3 className="h5 fw-bold mb-0 text-dark">{summary?.totalTemoignages || 0}</h3>
            <p className="extra-small text-muted mb-0 fw-bold">Témoignages</p>
          </div>
        </div>

        <div className="col-12 col-md-8 col-lg-3">
          <div className="card-custom p-3 text-center border-0 shadow-sm transition h-100 bg-lamp text-white rounded-4 d-flex flex-column align-items-center justify-content-center">
            <div className="bg-white bg-opacity-20 text-white rounded-circle mb-1 d-flex align-items-center justify-content-center" style={{ width: '35px', height: '35px' }}>
              <i className="bi bi-share fs-6"></i>
            </div>
            <h3 className="h5 fw-bold mb-0 text-white">{mostShared.reduce((acc, curr) => acc + (curr.nbreShare || curr.nbreshare || 0), 0)}</h3>
            <p className="extra-small text-white text-opacity-75 mb-0 fw-bold">Impact Partages</p>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Graphique Statut */}
        <div className="col-lg-4">
          <div className="card-custom border-0 shadow-sm p-4 rounded-4 bg-white h-100">
            <h6 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                <i className="bi bi-pie-chart-fill text-primary"></i> Statut des Publications
            </h6>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.name === 'Publiées' ? '#1700E2' : '#94a3b8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-center">
                <p className="extra-small text-muted mb-0">Répartition entre Brouillons et Publiées</p>
            </div>
          </div>
        </div>

        {/* Top 5 Shared */}
        <div className="col-lg-8">
          <div className="card-custom border-0 shadow-sm p-4 rounded-4 bg-white h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-trophy-fill text-warning"></i> Top 5 Partages
                </h6>
                <span className="badge bg-light text-muted border py-2 px-3 fw-normal small">Plus populaires</span>
            </div>
            
            <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                        <tr className="extra-small text-uppercase text-muted border-0">
                            <th className="border-0 rounded-start" style={{ fontSize: '0.7rem' }}>Méditation</th>
                            <th className="border-0" style={{ fontSize: '0.7rem' }}>Type</th>
                            <th className="border-0 text-center rounded-end" style={{ fontSize: '0.7rem' }}>Partages</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mostShared.length > 0 ? (
                            mostShared.map((m, idx) => (
                                <tr key={idx} className="border-light border-bottom">
                                    <td className="py-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="bg-primary bg-opacity-10 text-primary small fw-bold rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '30px', height: '30px' }}>
                                                {idx + 1}
                                            </div>
                                            <span className="small text-dark fw-medium text-truncate-2" style={{ maxWidth: '300px' }}>
                                                {m.textMedit || m.textmedit || "Sans titre"}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge bg-light text-muted border small" style={{ fontSize: '0.65rem' }}>{m.type || "N/A"}</span>
                                    </td>
                                    <td className="text-center">
                                        <div className="d-flex flex-column align-items-center">
                                            <span className="fw-bold text-primary">{m.nbreShare || m.nbreshare || 0}</span>
                                            <i className="bi bi-caret-up-fill text-success" style={{ fontSize: '0.6rem' }}></i>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3" className="text-center py-4 text-muted small italic">Aucune donnée de partage disponible.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Statistiques;